var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMemo } from "react";
import { useChartTimeline, percentToDisplayTime, displayTimeToPercent, getTimelineBoundaryTimes, } from "./chart-timeline";
import { selectors, useAppSelector } from "store";
import { chartDrawingAreaWidth } from "ui/components/panels/graphs/constants";
// Minimum bar width in pixels — ensures tiny intervals remain visible and clickable.
var MIN_INTERVAL_PX = 8;
export var useChartElementPositions = function () {
    var timeline = useChartTimeline();
    var atmReleaseTime = useAppSelector(selectors.selectAtmReleaseTime);
    return useMemo(function () {
        if (!timeline)
            return null;
        var toNaturalPx = function (seconds) {
            return (seconds / timeline.totalTime) * chartDrawingAreaWidth;
        };
        var cumulativeOffset = 0;
        // Sequential: accumulates cumulativeOffset when interval is expanded to MIN_INTERVAL_PX,
        // so all later intervals are shifted right to avoid visual overlap.
        var processSequentialInterval = function (interval) {
            var naturalX1 = toNaturalPx(interval.begin);
            var naturalWidth = toNaturalPx(interval.end) - naturalX1;
            var actualWidth = interval.begin === interval.end
                ? 0
                : Math.max(naturalWidth, MIN_INTERVAL_PX);
            var adjustedX1 = naturalX1 + cumulativeOffset;
            var adjustedX2 = adjustedX1 + actualWidth;
            // If this interval was expanded, push the extra pixels forward so subsequent
            // intervals don't overlap. If naturalWidth >= MIN_INTERVAL_PX, delta is 0.
            cumulativeOffset += actualWidth - naturalWidth;
            return { x1: adjustedX1, x2: adjustedX2 };
        };
        // Parallel: uses a snapshotted offset from a specific moment in the sequential row,
        // so the movement bars align with their corresponding air-row start points.
        var processParallelInterval = function (interval, offset) {
            var naturalX1 = toNaturalPx(interval.begin);
            var naturalWidth = toNaturalPx(interval.end) - naturalX1;
            var actualWidth = interval.begin === interval.end
                ? 0
                : Math.max(naturalWidth, MIN_INTERVAL_PX);
            return {
                x1: naturalX1 + offset,
                x2: naturalX1 + offset + actualWidth,
            };
        };
        // Air row in sequential time order
        var vacuumOnBeforePick = processSequentialInterval(timeline.vacuumOnBeforePick);
        var blowOffOnBeforePick = processSequentialInterval(timeline.blowOffOnBeforePick);
        var timeToPartSecured = processSequentialInterval(timeline.timeToPartSecured);
        var delayToEnergySave;
        if (timeline.delayToEnergySave &&
            timeline.delayToEnergySave.begin !== timeline.delayToEnergySave.end) {
            delayToEnergySave = processSequentialInterval(timeline.delayToEnergySave);
        }
        var timeWithEnergySavingOn;
        var timeWithEnergySavingOff;
        if (timeline.timeWithEnergySavingOn) {
            timeWithEnergySavingOn = processSequentialInterval(timeline.timeWithEnergySavingOn);
        }
        else if (timeline.timeWithEnergySavingOff) {
            timeWithEnergySavingOff = processSequentialInterval(timeline.timeWithEnergySavingOff);
        }
        var releaseTimeToPlacePart = processSequentialInterval(timeline.releaseTimeToPlacePart);
        var offsetAtReturnStart = cumulativeOffset;
        var additionalReleaseTime = processSequentialInterval(timeline.additionalReleaseTime);
        // Movement row: parallel bars run alongside the air row.
        //
        // The transfer bar spans from part-secured to the start of release —
        // the same span the air-row energy-saving bars occupy. Anchor both of
        // its ends directly to the surrounding air-row boundaries
        // (timeToPartSecured.x2 and releaseTimeToPlacePart.x1) instead of to a
        // start-time offset snapshot. A snapshot only captures expansion that
        // happened before the bar starts, so when the small ES sub-bars
        // (delayToEnergySave / timeWithEnergySavingOn) are floored to
        // MIN_INTERVAL_PX their extra pixels were left out of transfer.x2 — and
        // the "Place" line drawn at it fell short of the ES bar's right edge.
        var transfer = {
            x1: timeToPartSecured.x2,
            x2: releaseTimeToPlacePart.x1,
        };
        var returnTime = processParallelInterval(timeline.returnTime, offsetAtReturnStart);
        var positions = {
            vacuumOnBeforePick: vacuumOnBeforePick,
            blowOffOnBeforePick: blowOffOnBeforePick,
            timeToPartSecured: timeToPartSecured,
            delayToEnergySave: delayToEnergySave,
            timeWithEnergySavingOn: timeWithEnergySavingOn,
            timeWithEnergySavingOff: timeWithEnergySavingOff,
            transfer: transfer,
            releaseTimeToPlacePart: releaseTimeToPlacePart,
            additionalReleaseTime: additionalReleaseTime,
            returnTime: returnTime,
        };
        var timePixelMap = buildTimePixelMap(positions, timeline);
        // The cup-release ATM moment as a play fraction, and its pixel in the
        // cascaded timeline, so the SVG ATM marker, the play-head ATM snap and
        // the recharts ATM reference line all resolve to the same instant.
        var atmPercent = atmReleaseTime !== undefined
            ? displayTimeToPercent(atmReleaseTime, timeline)
            : undefined;
        var atmVisualPixel = atmPercent !== undefined
            ? timePercentToVisualPixel(atmPercent, timePixelMap)
            : positions.releaseTimeToPlacePart.x2;
        return {
            positions: positions,
            timeline: timeline,
            timePixelMap: timePixelMap,
            atmPercent: atmPercent,
            atmVisualPixel: atmVisualPixel,
            percentToDisplayTime: function (percent) {
                return percentToDisplayTime(percent, timeline);
            },
            displayTimeToPercent: function (time) {
                return displayTimeToPercent(time, timeline);
            },
        };
    }, [timeline, atmReleaseTime]);
};
/**
 * The play-location fractions (0–1) the play head snaps to while scrubbing: the
 * cycle's key-point boundaries, but with the timeline's release-end boundary
 * replaced by the data-derived cup-release `atmPercent` when available. This
 * keeps "ATM" snapping to the exact moment the charts mark (the recharts ATM
 * reference line), rather than the slightly-earlier timeline release boundary.
 */
export var getPlayLineSnapPercents = function (data) {
    var timeline = data.timeline, atmPercent = data.atmPercent;
    var boundaryPercents = getTimelineBoundaryTimes(timeline).map(function (time) { return time / timeline.totalTime; });
    if (atmPercent === undefined)
        return boundaryPercents;
    var releaseEndPercent = timeline.releaseTimeToPlacePart.end / timeline.totalTime;
    return __spreadArray(__spreadArray([], __read(boundaryPercents.filter(function (percent) { return percent !== releaseEndPercent; })), false), [
        atmPercent,
    ], false);
};
/**
 * Builds ordered timePixelMap mapping [timePercent, visualX] for piecewise
 * interpolation between the cascaded timeline bar and true time fractions.
 */
export var buildTimePixelMap = function (positions, timeline) {
    var totalTime = timeline.totalTime;
    var timePixelMap = [[0, 0]];
    var addInterval = function (interval, pos) {
        timePixelMap.push([interval.begin / totalTime, pos.x1]);
        timePixelMap.push([interval.end / totalTime, pos.x2]);
    };
    addInterval(timeline.vacuumOnBeforePick, positions.vacuumOnBeforePick);
    addInterval(timeline.blowOffOnBeforePick, positions.blowOffOnBeforePick);
    addInterval(timeline.timeToPartSecured, positions.timeToPartSecured);
    if (timeline.delayToEnergySave && positions.delayToEnergySave) {
        addInterval(timeline.delayToEnergySave, positions.delayToEnergySave);
    }
    if (timeline.timeWithEnergySavingOn && positions.timeWithEnergySavingOn) {
        addInterval(timeline.timeWithEnergySavingOn, positions.timeWithEnergySavingOn);
    }
    else if (timeline.timeWithEnergySavingOff &&
        positions.timeWithEnergySavingOff) {
        addInterval(timeline.timeWithEnergySavingOff, positions.timeWithEnergySavingOff);
    }
    addInterval(timeline.releaseTimeToPlacePart, positions.releaseTimeToPlacePart);
    addInterval(timeline.additionalReleaseTime, positions.additionalReleaseTime);
    addInterval(timeline.returnTime, positions.returnTime);
    // Deduplicate consecutive entries with the same visualX
    return timePixelMap.filter(function (_a, i, arr) {
        var _b = __read(_a, 2), x = _b[1];
        return i === 0 || x !== arr[i - 1][1];
    });
};
/**
 * Converts a visual pixel position in the cascaded timeline bar to a true
 * time percentage (0–1) relative to totalTime. Uses piecewise linear
 * interpolation over the cascade timePixelMap so that the result is accurate
 * even when events have been expanded to the minimum pixel width.
 *
 * Coordinate space: `pixel` is an offset in visual pixels from the left edge
 * of the chart drawing area. `timePixelMap` is the precomputed [timePercent,
 * visualPixel] mapping from `ChartElementPositionsData.timePixelMap`.
 *
 * Use this when converting a click position to a time fraction (e.g., for
 * setting the play head or computing zoom boundaries).
 */
export var visualPixelToTimePercent = function (pixel, timePixelMap) {
    // Piecewise linear interpolation
    for (var i = 0; i < timePixelMap.length - 1; i++) {
        var _a = __read(timePixelMap[i], 2), t0 = _a[0], x0 = _a[1];
        var _b = __read(timePixelMap[i + 1], 2), t1 = _b[0], x1 = _b[1];
        if (pixel >= x0 && pixel <= x1) {
            var fraction = x1 === x0 ? 0 : (pixel - x0) / (x1 - x0);
            return t0 + fraction * (t1 - t0);
        }
    }
    // Beyond the last timePixelMap entry: extrapolate using the last segment's scale
    if (timePixelMap.length >= 2) {
        var _c = __read(timePixelMap[timePixelMap.length - 2], 2), t0 = _c[0], x0 = _c[1];
        var _d = __read(timePixelMap[timePixelMap.length - 1], 2), t1 = _d[0], x1 = _d[1];
        var fraction = x1 === x0 ? 1 : (pixel - x0) / (x1 - x0);
        return Math.min(1, t0 + fraction * (t1 - t0));
    }
    return pixel / chartDrawingAreaWidth;
};
/**
 * Converts a true time percentage (0–1) back to the visual pixel position in
 * the cascaded timeline bar. This is the inverse of `visualPixelToTimePercent`
 * and accounts for the minimum-pixel-width expansion of small intervals.
 *
 * Coordinate space: the returned value is a visual pixel offset from the left
 * edge of the chart drawing area. `timePixelMap` is the precomputed [timePercent,
 * visualPixel] mapping from `ChartElementPositionsData.timePixelMap`.
 *
 * Use this when positioning the play head (mapping stored time fraction →
 * screen pixel) or when inverting a zoom domain back to visual coordinates.
 */
export var timePercentToVisualPixel = function (timePercent, timePixelMap) {
    for (var i = 0; i < timePixelMap.length - 1; i++) {
        var _a = __read(timePixelMap[i], 2), t0 = _a[0], x0 = _a[1];
        var _b = __read(timePixelMap[i + 1], 2), t1 = _b[0], x1 = _b[1];
        if (timePercent >= t0 && timePercent <= t1) {
            var fraction = t1 === t0 ? 0 : (timePercent - t0) / (t1 - t0);
            return x0 + fraction * (x1 - x0);
        }
    }
    // Beyond the last timePixelMap entry
    if (timePixelMap.length >= 2) {
        var _c = __read(timePixelMap[timePixelMap.length - 2], 2), t0 = _c[0], x0 = _c[1];
        var _d = __read(timePixelMap[timePixelMap.length - 1], 2), t1 = _d[0], x1 = _d[1];
        var fraction = t1 === t0 ? 1 : (timePercent - t0) / (t1 - t0);
        // Clamp to the last timePixelMap entry's pixel (= positions.returnTime.x2)
        var maxX = timePixelMap[timePixelMap.length - 1][1];
        return Math.min(maxX, x0 + fraction * (x1 - x0));
    }
    return timePercent * chartDrawingAreaWidth;
};
